import { useState, useEffect, useCallback, useRef } from 'react';
import { createWorkflow, getWorkflow, updateWorkflow } from '../generated/api/sdk.gen.js';
import type {
  WorkflowDefinition,
  WorkflowEdge as ApiWorkflowEdge,
  WorkflowTemplateResult,
  WorkflowStepPayload,
} from '../generated/api/types.gen.js';
import type { WorkflowBuilderStep, WorkflowEdgeForm, FlowDirection } from '../pages/admin/WorkflowBuilderTypes.js';
import { emptyStep, DEFAULT_FORM_STATE } from '../pages/admin/WorkflowBuilderTypes.js';
import type { WorkflowFormState } from '../pages/admin/WorkflowBuilderTypes.js';
import { useToast } from '../components/ui/index.js';
import { useDirtyGuard, type DirtyGuardResult } from './useDirtyGuard.js';

// ==========================================
// Deep equality check for dirty detection
// ==========================================

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (typeof a === 'object') {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

// ==========================================
// Diff computation
// ==========================================

export interface FieldDiff {
  path: string;
  label: string;
  previous: unknown;
  current: unknown;
  type: 'changed' | 'added' | 'removed';
}

function computeDiff(current: WorkflowFormState, saved: WorkflowFormState): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  const topFields: Array<{ key: keyof WorkflowFormState; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
    { key: 'status', label: 'Status' },
    { key: 'tags', label: 'Tags' },
    { key: 'cronSchedule', label: 'Cron Schedule' },
    { key: 'scheduleEnabled', label: 'Schedule Enabled' },
    { key: 'flowDirection', label: 'Flow Direction' },
  ];

  for (const { key, label } of topFields) {
    if (!deepEqual(current[key], saved[key])) {
      diffs.push({ path: key, label, previous: saved[key], current: current[key], type: 'changed' });
    }
  }

  // Step diffs
  const savedStepMap = new Map(saved.steps.map((s) => [s.id.toString(), s]));
  const currentStepMap = new Map(current.steps.map((s) => [s.id.toString(), s]));

  for (const step of current.steps) {
    const stepKey = step.id.toString();
    const savedStep = savedStepMap.get(stepKey);
    if (!savedStep) {
      diffs.push({
        path: `steps.${stepKey}`,
        label: `Step: ${step.name || 'Unnamed'}`,
        previous: null,
        current: step,
        type: 'added',
      });
    } else if (!deepEqual(step, savedStep)) {
      diffs.push({
        path: `steps.${stepKey}`,
        label: `Step: ${step.name || savedStep.name}`,
        previous: savedStep,
        current: step,
        type: 'changed',
      });
    }
  }

  for (const step of saved.steps) {
    if (!currentStepMap.has(step.id.toString())) {
      diffs.push({
        path: `steps.${step.id.toString()}`,
        label: `Step: ${step.name || 'Unnamed'}`,
        previous: step,
        current: null,
        type: 'removed',
      });
    }
  }

  return diffs;
}

// ==========================================
// localStorage helpers
// ==========================================

const STORAGE_PREFIX = 'workflow-draft-';

function getDraftKey(id: string): string {
  return `${STORAGE_PREFIX}${id}`;
}

interface DraftEnvelope {
  version?: number;
  form: WorkflowFormState;
}

function loadDraft(id: string): DraftEnvelope | null {
  try {
    const raw = localStorage.getItem(getDraftKey(id));
    if (!raw) return null;
    return JSON.parse(raw) as DraftEnvelope;
  } catch {
    return null;
  }
}

function saveDraft(id: string, state: WorkflowFormState): void {
  try {
    const envelope: DraftEnvelope = { version: state.version, form: state };
    localStorage.setItem(getDraftKey(id), JSON.stringify(envelope));
  } catch {
    /* quota exceeded — silently skip */
  }
}

function clearDraft(id: string): void {
  localStorage.removeItem(getDraftKey(id));
}

// ==========================================
// Parse API response into form state
// ==========================================

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseApiResponse(template: WorkflowDefinition): WorkflowFormState {
  // Map server WorkflowEdge[] → WorkflowEdgeForm[]
  const rawEdges: ApiWorkflowEdge[] = template.edges ?? [];
  const edges: WorkflowEdgeForm[] = rawEdges.map((e) => ({
    id: e.id,
    sourceStepId: e.sourceStepId,
    targetStepId: e.targetStepId,
    kind: e.kind ?? 'data',
    label: e.label ?? null,
  }));

  // The join fields (templateSlug, pinnedTools, ...) come back on every step in practice (see
  // the WorkflowBuilderStep doc comment in WorkflowBuilderTypes.ts) but are typed optional on the
  // generated step — default them so the local, stricter WorkflowBuilderStep shape is always satisfied.
  const steps: WorkflowBuilderStep[] = (template.steps ?? []).map((s) => ({
    ...s,
    templateSlug: s.templateSlug ?? null,
    pinnedTools: s.pinnedTools ?? [],
  }));

  // `schedule` is `unknown` on the wire (free-form jsonb) — narrow instead of trusting a cast.
  const schedule = isRecord(template.schedule) ? template.schedule : {};

  return {
    name: template.name ?? '',
    description: template.description ?? '',
    category: template.category ?? 'custom',
    status: template.status ?? 'draft',
    steps,
    edges,
    tags: (template.tags ?? []).join(', '),
    cronSchedule: typeof schedule.cron === 'string' ? schedule.cron : '',
    scheduleEnabled: typeof schedule.enabled === 'boolean' ? schedule.enabled : false,
    flowDirection: template.flowDirection === 'horizontal' ? 'horizontal' : 'vertical',
    version: template.version ?? undefined,
  };
}

function hasContractProperties(contract: unknown): boolean {
  if (!contract || typeof contract !== 'object' || Array.isArray(contract)) return false;
  const schema =
    (contract as { jsonSchema?: unknown; schema?: unknown }).jsonSchema ??
    (contract as { jsonSchema?: unknown; schema?: unknown }).schema;
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return false;
  const properties = (schema as { properties?: unknown }).properties;
  return Boolean(
    properties && typeof properties === 'object' && !Array.isArray(properties) && Object.keys(properties).length > 0,
  );
}

function isProcessorStep(step: WorkflowBuilderStep): boolean {
  const outputConfig = step.outputConfig as { processor?: unknown; llmTransform?: unknown } | null;
  const processor = outputConfig?.processor;
  const llmTransform = outputConfig?.llmTransform;
  return Boolean(
    (processor && typeof processor === 'object' && !Array.isArray(processor)) ||
      (llmTransform && typeof llmTransform === 'object' && !Array.isArray(llmTransform)),
  );
}

// ==========================================
// Hook
// ==========================================

export interface ConflictState {
  serverState: WorkflowFormState;
  message: string;
}

export interface SaveWarningAction {
  label: string;
  /** Which tab to navigate to, or 'negotiate' for contract negotiation */
  type: 'navigate' | 'negotiate';
  tab?: 'config' | 'prompt' | 'output' | 'contract' | 'debug';
}

export interface SaveWarning {
  stepId: string;
  stepName: string;
  message: string;
  /** Optional fix action — shown as a button in the warnings dialog */
  action?: SaveWarningAction;
}

/**
 * The exact shape `getFormData` builds and both `createWorkflow`/`updateWorkflow` accept as
 * `body` — structurally compatible with the generated `WorkflowCreate` and `WorkflowUpdate`
 * (schedule satisfies its `{ [key: string]: unknown } | null` shape). `steps` is the generated
 * `WorkflowStepPayload` (the wire type), not the builder's local `WorkflowBuilderStep` — that type has
 * no index signature of its own, so TS refuses it where `WorkflowStepPayload`'s is required.
 */
export interface WorkflowSavePayload {
  name: string;
  description: string;
  category: string;
  status: string;
  steps: WorkflowStepPayload[];
  flowDirection: FlowDirection;
  tags: string[];
  schedule: { type: 'cron'; cron: string; timezone: string; enabled: boolean } | null;
  version?: number;
}

export interface UseWorkflowFormReturn {
  form: WorkflowFormState;
  savedState: WorkflowFormState | null;
  isDirty: boolean;
  /**
   * Navigate-away guard from `useDirtyGuard`. The HOST PAGE must render
   * `<NavigateAwayWarning open={navGuard.pendingNavigation} onLeave={navGuard.confirmLeave}
   * onStay={navGuard.cancelLeave} />`; the hook blocks the navigation, the page asks the question.
   * `confirmLeave` is wrapped to clear the local draft before navigating.
   */
  navGuard: DirtyGuardResult;
  loading: boolean;
  updateField: <K extends keyof WorkflowFormState>(field: K, value: WorkflowFormState[K]) => void;
  updateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  addStep: () => WorkflowBuilderStep;
  removeStep: (stepId: string) => void;
  setSteps: (steps: WorkflowBuilderStep[]) => void;
  setForm: (state: WorkflowFormState) => void;
  addEdge: (edge: WorkflowEdgeForm) => void;
  removeEdge: (sourceStepId: number, targetStepId: number) => void;
  save: (navigate: (path: string) => void) => Promise<void>;
  /** Save full workflow data without navigating; returns the new server version or null on error. */
  saveNoNavigate: () => Promise<number | null>;
  saving: boolean;
  discardDraft: () => void;
  getDiff: () => FieldDiff[];
  getFormData: () => WorkflowSavePayload;
  conflict: ConflictState | null;
  resolveConflict: (action: 'reload' | 'force') => Promise<void>;
  /** Validate the workflow before save. Returns warnings (not blocking errors). */
  validate: () => SaveWarning[];
}

export function useWorkflowForm(id: string | undefined): UseWorkflowFormReturn {
  const effectiveId = id && id !== 'new' ? id : 'new';
  const isNew = effectiveId === 'new';

  const [form, setFormState] = useState<WorkflowFormState>(DEFAULT_FORM_STATE);
  const { notify } = useToast();
  const [savedState, setSavedState] = useState<WorkflowFormState | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<ConflictState | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist draft to localStorage while editing (for unsaved change recovery)
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      saveDraft(effectiveId, form);
    }, 300);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [form, effectiveId]);

  // Load from API or localStorage on mount
  useEffect(() => {
    if (isNew) {
      const draft = loadDraft('new');
      if (draft) setFormState(draft.form);
      setSavedState(DEFAULT_FORM_STATE);
      return;
    }

    getWorkflow({ path: { id: String(effectiveId) } })
      .then((res) => {
        const t = res.data?.template;
        if (t) {
          const serverState = parseApiResponse(t);
          setSavedState(serverState);

          // Check for a versioned draft with unsaved changes
          const draft = loadDraft(effectiveId);
          if (draft && draft.version === serverState.version && !deepEqual(draft.form, serverState)) {
            // Same version base, user had pending changes — safe to restore
            setFormState(draft.form);
          } else {
            // No draft, stale draft (different version), or draft matches server — use server
            setFormState(serverState);
            if (draft) clearDraft(effectiveId);
          }
        }
      })
      .finally(() => setLoading(false));
  }, [effectiveId, isNew]);

  const isDirty = savedState !== null && !deepEqual(form, savedState);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const effectiveIdRef = useRef(effectiveId);
  effectiveIdRef.current = effectiveId;

  // Browser beforeunload — warn when closing tab/refreshing with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // In-app navigation guard.
  //
  // This used to hand-roll click-interception and popstate handling here, each ending in a
  // `window.confirm('You have unsaved changes. Leave without saving?')`. That was a second copy
  // of `useDirtyGuard`, which exists for exactly this and is already adopted by six pages
  // (NewToolPage, ToolEditor, NewTemplatePage, TemplateEditor, NewDaemonPage, NewConversationPage)
  // — its own header says "no more `window.confirm()`". A native confirm also blocks the event
  // loop, so the browser-automation suite cannot drive past it, and it cannot be styled.
  //
  // The one behaviour this hook adds over the shared guard is clearing the local draft when the
  // user chooses to leave, so `confirmLeave` is wrapped rather than re-implemented.
  const dirtyGuard = useDirtyGuard(isDirty);
  const confirmLeave = useCallback(() => {
    clearDraft(effectiveIdRef.current);
    dirtyGuard.confirmLeave();
  }, [dirtyGuard]);

  const updateField = useCallback(<K extends keyof WorkflowFormState>(field: K, value: WorkflowFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateStep = useCallback((stepId: string, updates: Partial<WorkflowBuilderStep>) => {
    setFormState((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.id.toString() === stepId ? { ...s, ...updates } : s)),
    }));
  }, []);

  const addStep = useCallback((): WorkflowBuilderStep => {
    const step = emptyStep(1);
    setFormState((prev) => {
      step.order = prev.steps.length + 1;
      step.name = `Step ${prev.steps.length + 1}`;
      // Assign a temporary unique ID (negative, server replaces on save)
      step.id = -(Date.now() + Math.floor(Math.random() * 1000));
      return { ...prev, steps: [...prev.steps, step] };
    });
    return step;
  }, []);

  const removeStep = useCallback((stepId: string) => {
    setFormState((prev) => ({ ...prev, steps: prev.steps.filter((s) => s.id.toString() !== stepId) }));
  }, []);

  const setSteps = useCallback((steps: WorkflowBuilderStep[]) => {
    setFormState((prev) => ({ ...prev, steps }));
  }, []);

  const setForm = useCallback((state: WorkflowFormState) => {
    setFormState(state);
  }, []);

  const addEdge = useCallback((edge: WorkflowEdgeForm) => {
    setFormState((prev) => ({
      ...prev,
      edges: [...prev.edges, edge],
    }));
  }, []);

  const removeEdge = useCallback((sourceStepId: number, targetStepId: number) => {
    setFormState((prev) => ({
      ...prev,
      edges: prev.edges.filter((e) => !(e.sourceStepId === sourceStepId && e.targetStepId === targetStepId)),
    }));
  }, []);

  const getFormData = useCallback((): WorkflowSavePayload => {
    return {
      name: form.name,
      description: form.description,
      category: form.category,
      status: form.status,
      steps: form.steps.map((s) => ({ ...s })),
      flowDirection: form.flowDirection,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      schedule: form.cronSchedule
        ? { type: 'cron', cron: form.cronSchedule, timezone: 'UTC', enabled: form.scheduleEnabled }
        : null,
      version: form.version,
    };
  }, [form]);

  const validate = useCallback((): SaveWarning[] => {
    const warnings: SaveWarning[] = [];
    for (const step of form.steps) {
      const stepId = step.id.toString();
      const name = step.name || stepId;
      if (!isProcessorStep(step) && !step.templateSlug && !step.templateId) {
        warnings.push({
          stepId,
          stepName: name,
          message: 'No agent template assigned',
          action: { label: 'Configure', type: 'navigate', tab: 'config' },
        });
      }
      if (!hasContractProperties(step.outputContract)) {
        warnings.push({
          stepId,
          stepName: name,
          message: 'No output contract',
          action: { label: 'Negotiate', type: 'negotiate' },
        });
      }
      if (step.order > 1 && !step.inputContracts) {
        warnings.push({
          stepId,
          stepName: name,
          message: 'No input contracts for upstream steps',
          action: { label: 'Negotiate', type: 'negotiate' },
        });
      }
    }
    if (form.steps.length === 0) {
      warnings.push({ stepId: '', stepName: 'Workflow', message: 'No steps defined' });
    }
    if (!form.name.trim()) {
      warnings.push({ stepId: '', stepName: 'Workflow', message: 'Workflow name is empty' });
    }
    return warnings;
  }, [form]);

  const saveNoNavigate = useCallback(async (): Promise<number | null> => {
    setSaving(true);
    try {
      const data = getFormData();
      const res = isNew
        ? await createWorkflow({ body: data })
        : await updateWorkflow({ path: { id: String(effectiveId) }, body: data });
      const result = res.data;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      const serverState = result?.template ? parseApiResponse(result.template) : { ...form };
      setSavedState(serverState);
      setFormState(serverState);
      clearDraft(effectiveId);
      if (isNew) clearDraft('new');
      setConflict(null);
      return serverState.version ?? null;
    } catch (err) {
      const msg = (err as Error).message ?? '';
      if (msg.includes('409') || msg.toLowerCase().includes('modified by another')) {
        try {
          const d = (await getWorkflow({ path: { id: String(effectiveId) } })).data;
          setConflict({ serverState: parseApiResponse(d!.template), message: msg });
        } catch {
          setConflict({ serverState: form, message: msg });
        }
      } else {
        notify('Save failed: ' + msg, 'danger');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, isNew, effectiveId, getFormData]);

  const save = useCallback(
    async (navigate: (path: string) => void) => {
      setSaving(true);
      try {
        const data = getFormData();
        let result: WorkflowTemplateResult | undefined;
        if (isNew) {
          const res = await createWorkflow({ body: data });
          result = res.data;
        } else {
          const res = await updateWorkflow({ path: { id: String(effectiveId) }, body: data });
          result = res.data;
        }
        // Cancel pending debounce so it can't re-save a stale draft
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          debounceTimer.current = null;
        }
        // Use server response as new savedState (has incremented version)
        const serverState = result?.template ? parseApiResponse(result.template) : { ...form };
        setSavedState(serverState);
        setFormState(serverState);
        clearDraft(effectiveId);
        if (isNew) clearDraft('new');
        setConflict(null);
        navigate('/agents/workflows');
      } catch (err) {
        const msg = (err as Error).message;
        if (msg.includes('409') || msg.toLowerCase().includes('modified by another')) {
          // Version conflict — fetch current server state and show conflict dialog
          try {
            const d = (await getWorkflow({ path: { id: String(effectiveId) } })).data;
            const serverState = parseApiResponse(d!.template);
            setConflict({ serverState, message: msg });
          } catch {
            setConflict({ serverState: form, message: msg });
          }
        } else {
          notify('Save failed: ' + msg, 'danger');
        }
      } finally {
        setSaving(false);
      }
    },
    [form, isNew, effectiveId, getFormData],
  );

  const discardDraft = useCallback(() => {
    if (savedState) {
      setFormState(savedState);
      clearDraft(effectiveId);
    }
  }, [savedState, effectiveId]);

  const getDiff = useCallback((): FieldDiff[] => {
    if (!savedState) return [];
    return computeDiff(form, savedState);
  }, [form, savedState]);

  const resolveConflict = useCallback(
    async (action: 'reload' | 'force') => {
      if (!conflict) return;
      if (action === 'reload') {
        // Discard local changes, use server state
        setFormState(conflict.serverState);
        setSavedState(conflict.serverState);
        clearDraft(effectiveId);
        setConflict(null);
      } else {
        // Force save with the current server version
        setFormState((prev) => ({ ...prev, version: conflict.serverState.version }));
        setConflict(null);
        // The next save() call will use the updated version
      }
    },
    [conflict, effectiveId],
  );

  return {
    form,
    savedState,
    isDirty,
    /**
     * Navigate-away guard. The HOST PAGE must render
     * `<NavigateAwayWarning open={navGuard.pendingNavigation} onLeave={navGuard.confirmLeave}
     * onStay={navGuard.cancelLeave} />` — the hook blocks the navigation, the page asks the
     * question. Without that render the guard silently blocks and never resolves.
     */
    navGuard: { ...dirtyGuard, confirmLeave },
    loading,
    updateField,
    updateStep,
    addStep,
    removeStep,
    setSteps,
    setForm,
    addEdge,
    removeEdge,
    save,
    saveNoNavigate,
    saving,
    validate,
    discardDraft,
    getDiff,
    getFormData,
    conflict,
    resolveConflict,
  };
}
